import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import debounce from 'lodash.debounce'
import { getNewRiskScoresWithAssets } from 'api'
import {
    Container,
    DataGridTable,
    HeaderBar,
    Blade,
    Icon,
    TypoGraph,
    Divider,
    Card,
    MultiLineChart,
    Chip,
    Spinner,
  } from 'components'
import { assetActivityList, applyCommercialAutoVisibility, applyVehicleClassDefault } from 'constants/tableData'
import moment from 'moment'
import styles from './insuranceCarriers.module.scss'
import colors from 'theme/colors'
import strings from 'strings'
import { forEach } from 'lodash'
import { useGridApiRef } from '@mui/x-data-grid'
import { isInsuranceLogin } from 'tools/auth'
import { exportTableAsCSV, formatDateMMDDYYYY } from 'tools/helper'
import { reportException } from 'tools/logs'
import AssetActivityScoringToolbar from './assetActivityScoringToolbar'

const t = strings.CARRIERS
const e = strings.CSV_EXPORT
interface IInsuranceCarrierProps {
    print?: boolean
}

interface ISummaryData {
    totalAssets: number;
    totalAssetsPrev: number;
    totalAssetsVariation: number;
    riskIndex: number;
    riskIndexPrev: number;
    riskVariation: number;
    geographicIndex: number;
    geographicIndexPrev: number;
    geographicVariation: number;
    speedIndex: number;
    speedIndexPrev: number;
    speedVariation: number;
    harshEventsIndex: number;
    harshEventsIndexPrev: number;
    harshEventsVariation: number;
}

const InsuranceCarriers: React.FC<IInsuranceCarrierProps> = (props) => {
    const { print } = props
    const [loading, setLoading] = useState(false)
    // Keep the asset table (headers included) loading until the first fetch
    // completes, so the flag-dependent 85+ header does not flash in.
    const [assetsReady, setAssetsReady] = useState(false)
    const wasLoadingRef = useRef(false)
    useEffect(() => {
      if (wasLoadingRef.current && !loading) setAssetsReady(true)
      wasLoadingRef.current = loading
    }, [loading])
    const [showAssetDetails, setShowAssetDetails] = useState(false)
    const [selectedRow, setSelectedRow] = useState<any>()
    const [responseData, setResponseData] = useState<any>()
    const [riskData, setRiskData] = useState<any>([])
    const [riskAssetData, setRiskAssetData] = useState<any>([])
    const [fleetPerformanceData, setFleetPerformanceData] = useState<any>([])
    const [summaryData, setSummaryData] = useState<ISummaryData>()
    const [activityFilter, setActivityFilter] = useState<string>('active')
    const [searchText, setSearchText] = useState<string>('')
    const { column, groups } = useMemo(() => {
      const result = applyCommercialAutoVisibility(
        assetActivityList.column,
        assetActivityList.groups,
        responseData?.commercialAutoEnabled === true,
        true
      )
      const columns = applyVehicleClassDefault(
        result.columns,
        responseData?.defaultScoreType
      )
      return { column: columns, groups: result.groups }
    }, [responseData])
    const apiRef = useGridApiRef()

    const axisTitles = {
        yAxis: 'Index',
        xAxis: 'Score Date',
    };
    
    useEffect(() => {
      setLoading(true)
      getNewRiskScoresWithAssets()
        .then((res: any) => {
          setResponseData(res)
        })
        .catch((error: any) => {
          reportException(error)
        })
        .finally(() => {
          setLoading(false)
        })
    }, [])

    useEffect(() => {
      setSummaryData(responseData?.summary)
    }, [responseData])

    useEffect(() => {
      const filteredRiskData = responseData?.riskScores.filter((item: any) =>
        localStorage.getItem('eldProviderRisk')?.includes(item?.eldProvider)
      )
      setRiskData(filteredRiskData)
    }, [summaryData])

    useEffect(() => {
      const graphData = getPerformanceData(riskData)
      setFleetPerformanceData(graphData)
    }, [riskData])

    useEffect(() => {
      const assetsData = responseData?.assetsDetails.filter((item: any) =>
        localStorage.getItem('eldProviderRisk')?.includes(item?.eldProviderName)
      )
      if (assetsData?.length > 0) {
        const assetRiskScore = [...assetsData].sort(
          (a: any, b: any) => b.updated - a.updated
        )
        const uniqueAssets = new Set()
        const filteredAssets = assetRiskScore.filter((item: any) => {
          if (!uniqueAssets.has(item.assetEldId)) {
            uniqueAssets.add(item.assetEldId)
            return true
          }
          return false
        })
          .map((item: any) => ({
            ...item,
            executionDateFormatted: item.executionDate
              ? formatDateMMDDYYYY(item.executionDate)
              : null,
          }))
        setRiskAssetData(filteredAssets)
      }
    }, [fleetPerformanceData])

    const filteredData = useMemo(() => {
        if (!riskAssetData?.length) return []

        const now = new Date()
        const tenDaysAgo = new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000)

        const activityFilteredData = riskAssetData.filter((asset: any) => {
            const executionDate = asset.executionDate ? new Date(asset.executionDate) : null
            
            switch (activityFilter) {
                case 'active':
                    return executionDate && executionDate >= tenDaysAgo
                case 'inactive':
                    return !executionDate || executionDate < tenDaysAgo
                default:
                    return true
            }
        })

        if (!searchText) {
            return activityFilteredData
        }

        const searchTerm = searchText.toLowerCase()
        return activityFilteredData.filter((item: any) => {
            const values = Object.values(item)
            for (let value of values) {
                if (typeof value === 'string' && value.toLowerCase().includes(searchTerm)) {
                    return true
                }
            }
            return false
        })
    }, [riskAssetData, activityFilter, searchText])

    const debouncedSearchChange = useMemo(
        () => debounce((searchValue: string) => {
            setSearchText(searchValue)
        }, 1000),
        []
    )

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedSearchChange.cancel()
        }
    }, [debouncedSearchChange])

    const loadPagination = (pageNumber: number, pageSize: number) => {
        console.log(`pagenumber: ${pageNumber}, pageSize: ${pageSize}`)
    }

    function formatDate(inputDateString: string) {
        const inputDate = new Date(inputDateString);        
        const month = String(inputDate.getMonth() + 1).padStart(2, '0');
        const day = String(inputDate.getDate()).padStart(2, '0');
        
        return `${month}-${day}`;
    }

    const getPerformanceData = (riskData: any) => {
        let summaryArray: any = []
        let harshIndexArray: any = []
        let geoIndexArray: any = []
        let speedIndexArray: any = []
        forEach(riskData?.reverse(), (item: any) => {
            const rowDate = formatDate(item?.periodEndDate)
            summaryArray.push({
                'x': rowDate,
                'y': item?.fleetScore ?? 0
            })
            harshIndexArray.push({
                'x': rowDate,
                'y': item?.harsh.index ?? 0
            })
            geoIndexArray.push({
                'x': rowDate,
                'y': item?.geographical.index ?? 0
            })
            speedIndexArray.push({
                'x': rowDate,
                'y': item?.speed.index ?? 0
            })
        })
        
        const summaryData = {
            'id': 'Summary',
            'label': 'Summary',
            'color': '#8CC0E8',
            'data': summaryArray
        }
        const harshIndexData = {
            'id': 'Harsh Index',
            'label': 'Harsh Index',
            'color': '#F9706B',
            'data': harshIndexArray
        }
        const speedIndexData = {
            'id': 'Speeding Index',
            'label': 'Speeding Index',
            'color': '#FAE178',
            'data': speedIndexArray
        }
        const geoIndexData = {
            'id': 'Geo Index',
            'label': 'Geo Index',
            'color': '#89EBB0',
            'data': geoIndexArray
        }

        const fleetData = []
        fleetData.push(summaryData)
        fleetData.push(harshIndexData)
        fleetData.push(geoIndexData)
        fleetData.push(speedIndexData)

        return fleetData
    }

    const handleRiskExport = () => {
        const csvString = apiRef.current.getDataAsCsv()
        const csvRows = csvString.split('\n')
        const modifiedCsv = csvRows.slice(1).join('\n')
        exportTableAsCSV(modifiedCsv, e.ASSETS_CSV_EXPORT_NAME)
      }
  
      const tableMenuItems = [
        {
          label: e.DOWNLOAD_AS_CSV,
          key: 'export',
          action: () => {
            handleRiskExport()
          },
        },
      ]

    const ToolbarComponent = useCallback(
        (props: any) => (
            <AssetActivityScoringToolbar
                activityFilter={activityFilter}
                setActivityFilter={setActivityFilter}
                onSearchChange={debouncedSearchChange}
                menuItems={tableMenuItems}
                searchValue={searchText}
                loading={loading}
            />
        ),
        [activityFilter, setActivityFilter, debouncedSearchChange, loading]
    )

    const BladeContent = (_props: any) => {
        return (
          <Container className={styles.bladeBodyContainer}>
            <Container className={styles.bladeTabContainer}>
                
            </Container>
            <Container className={styles.documentsSection}>
              <TypoGraph variant="h6" content="Documents" />
              <Divider />
              
            </Container>
          </Container>
        )
    }

    const HeaderDom = () => {
        return (
            <Container className={styles.headerContainer}>
                <Container className={styles.headerTitleContainer}>
                    <TypoGraph
                        variant="h1"
                        sx={{ marginRight: '1rem' }}
                        mb={0}
                        content={(!isInsuranceLogin ? localStorage.getItem('carrierName') : 'Carrier Details') ?? 'Carrier Details'}
                    />
                    <Chip
                        label={`Updated ${moment().format('dddd, MMMM Do')}`}
                        variant="filled"
                        sx={{
                            backgroundColor: '#E1FAEB',
                            color: '#20693D',
                        }}
                    />
                </Container>
            </Container>
        )
    }

    return (
        <Container className={styles.insuranceBladeBodyContainer}>
            <Container>
                <HeaderBar dom={<HeaderDom />} />
                <TypoGraph
                    variant="h2"
                    sx={{ marginRight: '1rem', marginTop: '-15px', marginBottom: '1rem' }}
                    mb={0}
                    content={localStorage.getItem('eldProviderRisk') ?? ''}
                />
                {/* Summary Header */}
                <Container className={styles.summaryMainContainer}>
                    <Container className={styles.summaryHeaderContainer}>
                        {(loading && <Spinner />) || (
                        <Container className={styles.summaryList}>
                            {/* Summary */}
                            <Container className={styles.itemContainer}>
                                <Icon name="summary_carriers" size={32} />
                                <Container className={styles.summaryContentContainer}>
                                    <TypoGraph
                                        variant="subtitle2"
                                        color={colors.white}
                                        content="Summary"
                                    />
                                    <Container className={styles.summaryDataContainer}>
                                        <TypoGraph
                                        color={colors.white}
                                        content={summaryData?.riskIndex.toFixed(2)}
                                        />
                                    </Container>
                                </Container>
                            </Container>
                            {/* Harsh Index */}
                            <Container className={styles.itemContainer}>
                                <Icon name="summary_harsh" size={32} />
                                <Container className={styles.summaryContentContainer}>
                                <TypoGraph
                                    variant="subtitle2"
                                    color={colors.white}
                                    content="Harsh Index"
                                />
                                <Container className={styles.summaryDataContainer}>
                                    <TypoGraph
                                    color={colors.white}
                                    content={summaryData?.harshEventsIndex.toFixed(2)}
                                    />
                                </Container>
                                </Container>
                            </Container>
                            {/* Geo Index */}
                            <Container className={styles.itemContainer}>
                                <Icon name="summary_geo" size={32} />
                                <Container className={styles.summaryContentContainer}>
                                <TypoGraph
                                    variant="subtitle2"
                                    color={colors.white}
                                    content="Geo Index"
                                />
                                <Container className={styles.summaryDataContainer}>
                                    <TypoGraph
                                    color={colors.white}
                                    content={summaryData?.geographicIndex.toFixed(2)}
                                    />
                                </Container>
                                </Container>
                            </Container>
                            {/* Speed Index */}
                            <Container className={styles.itemContainer}>
                                <Icon name="summary_speed" size={32} />
                                <Container className={styles.summaryContentContainer}>
                                <TypoGraph
                                    variant="subtitle2"
                                    color={colors.white}
                                    content="Speed Index"
                                />
                                <Container className={styles.summaryDataContainer}>
                                    <TypoGraph
                                    color={colors.white}
                                    content={summaryData?.speedIndex.toFixed(2)}
                                    />
                                </Container>
                                </Container>
                            </Container>
                            {/* Active Assets */}
                            <Container className={styles.itemContainer}>
                                <Icon name="summary_assets" size={32} />
                                <Container className={styles.summaryContentContainer}>
                                <TypoGraph
                                    variant="subtitle2"
                                    color={colors.white}
                                    content="Active Assets"
                                />
                                <Container className={styles.summaryDataContainer}>
                                    <TypoGraph
                                    color={colors.white}
                                    content={riskAssetData?.length.toString()}
                                    />
                                </Container>
                                </Container>
                            </Container>
                            {/* Total Assets */}
                            <Container className={styles.itemContainer}>
                                <Icon name="summary_assets" size={32} />
                                <Container className={styles.summaryContentContainer}>
                                <TypoGraph
                                    variant="subtitle2"
                                    color={colors.white}
                                    content="Total Assets"
                                />
                                <Container className={styles.summaryDataContainer}>
                                    <TypoGraph
                                    color={colors.white}
                                    content={riskAssetData?.length.toString()}
                                    />
                                </Container>
                                </Container>
                            </Container>
                        </Container>
                        )}
                    </Container>
                </Container>
                {/* Fleet Graph */}
                <Container className={styles.chartContainer} sx={{marginBottom: '1rem'}}>
                    <Card containerClass="flexAlignCenter overflowVisible">
                    {(loading && <Spinner />) || (
                        <>
                            <TypoGraph content="Fleet Performance" />
                            <MultiLineChart data={fleetPerformanceData} axisTitles={axisTitles} />
                        </>
                    )}
                    </Card>
                </Container>
                {/* Asset Activity & Scoring */}
                {assetsReady ? (
                <DataGridTable
                    apiRef={apiRef}
                    sx={{
                        '& .MuiDataGrid-columnHeader': {
                            paddingLeft: '2px',
                            paddingRight: '2px',
                        },
                        '& .MuiDataGrid-columnHeader .descriptionText': {
                            fontSize: '0.66rem !important',
                        },
                        // All data cells: left-aligned + vertically centered
                        '& .MuiDataGrid-cell': {
                            display: 'flex !important',
                            alignItems: 'center !important',
                            justifyContent: 'flex-start !important',
                            paddingTop: '0 !important',
                            marginLeft: '0 !important',
                            marginRight: '0 !important',
                        },
                        '& .MuiDataGrid-cell > div': {
                            width: '100%',
                            textAlign: 'left !important',
                            marginLeft: '0 !important',
                            marginRight: '0 !important',
                        },
                        // Score columns keep centered numbers (declared after so they win)
                        '& .summaryScoreCell, & .safetyScoreCell, & .safetyScoreStart, & .speedingScoreCell, & .speedingScoreStart, & .geoScoreCell, & .geoScoreStart': {
                            paddingLeft: '2px !important',
                            paddingRight: '2px !important',
                            justifyContent: 'center !important',
                        },
                        '& .summaryScoreCell > div, & .safetyScoreCell > div, & .safetyScoreStart > div, & .speedingScoreCell > div, & .speedingScoreStart > div, & .geoScoreCell > div, & .geoScoreStart > div': {
                            textAlign: 'center !important',
                        },
                    }}
                    title={t.DETAILS_TAB.ASSET_ACTIVITY_SCORING}
                    caption={t.DETAILS_TAB.TABLE_CAPTION}
                    showCaption={true}
                    loading={loading}
                    data={filteredData || []}
                    columns={column}
                    columnGroupingModel={groups}
                    columnHeaderHeight={30}
                    columnVisibilityModel={{assetId: false}}
                    totalCount={filteredData?.length}
                    reduceHeight={260}
                    checkboxSelection={false}
                    multiSearchFilter={false}
                    onSingleQuickFilterChange={debouncedSearchChange}
                    getRowId={(row: any) => row?.assetId}
                    handlePagination={(pageNumber, pageSize) =>
                        loadPagination(pageNumber, pageSize)
                    }
                    menuList={tableMenuItems}
                    showFilter={false}
                    components={{ toolbar: ToolbarComponent }}
                />
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '260px',
                        }}
                    >
                        <Spinner />
                    </div>
                )}
                <Blade
                    title={selectedRow?.assetEldId}
                    open={showAssetDetails}
                    onClose={() => setShowAssetDetails(false)}
                    bodyNode={<BladeContent />}
                />
            </Container>
        </Container>
    )
}

export default InsuranceCarriers